/**
 * Shared hook for document upload functionality
 * Encapsulates common upload logic and file handling
 */

import { useRef } from 'react';
import { useFileUpload, type FileUploadHelpers } from '@/hooks/use-file-upload';
import { useFileInput } from '@/hooks/use-drag-and-drop';
import { FILE_UPLOAD_CONSTRAINTS } from '@/lib/config/document-types';
import { acceptStringFor } from '@/lib/constants/file-type-accept';
import { toast } from '@/lib/toast';

export interface DocumentUploadConfig {
  /**
   * The backend file-type slug this dialog uploads against. It is also the whole answer to
   * "what may the picker offer?" — `acceptStringFor` derives that from the backend's own table
   * (VALE-1150), so a config no longer restates the formats beside the slug that owns them.
   * The letterhead dialog is why that mattered: it sat one line below
   * `fileType: FileTypeSlug.LETTERHEAD` offering JPG, PNG, DOC and DOCX, none of which the
   * backend has ever accepted for a letterhead.
   */
  fileType: string;
  maxFiles: number;
  isCertifiedCopy?: boolean;
}

export interface DocumentUploadCallbacks {
  onUpload: (
    files: Array<{
      file: File;
      fileType: string;
      isCertifiedCopy: boolean;
    }>,
    /** Real per-file progress + abort (LF-4771). Callers may ignore it. */
    helpers: FileUploadHelpers,
    /**
     * Whether the lawyer chose to run extraction on this upload (VALE-757).
     * Uploading is filing; extraction reads the document and changes matter
     * data, so it is a separate, affirmative act. Dialogs that offer no
     * extraction choice never pass `true`.
     */
    extract: boolean
  ) => Promise<void>;
  onClose?: () => void;
}

export function useDocumentUpload(
  config: DocumentUploadConfig,
  callbacks: DocumentUploadCallbacks
) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // `useFileUpload` owns the upload loop and has no notion of the extraction
  // choice, so the decision made at click time is parked here for the callback
  // it invokes. Defaults to `false`: filing must never imply extraction.
  const extractChoiceRef = useRef(false);

  const {
    files,
    addFiles,
    removeFile,
    clearFiles,
    uploadFiles,
    isUploading,
    updateFileType,
    updateCertifiedCopy,
    validateFiles,
    hasFiles,
    canUpload
  } = useFileUpload({
    maxFiles: config.maxFiles,
    maxFileSize: FILE_UPLOAD_CONSTRAINTS.maxFileSize,
    autoDetectFileType: false,
    onUpload: async (uploadFiles, helpers) => {
      const filesToUpload = uploadFiles.map((f) => ({
        file: f.file,
        fileType: config.fileType,
        isCertifiedCopy: config.isCertifiedCopy ?? f.isCertifiedCopy ?? false
      }));
      // `file` identity is preserved through the map, so `helpers.onFileProgress`
      // still resolves each row for the caller.
      await callbacks.onUpload(
        filesToUpload,
        helpers,
        extractChoiceRef.current
      );
    }
  });

  const handleFilesSelected = (newFiles: File[]) => {
    const { valid, invalid } = validateFiles(newFiles);

    if (invalid.length > 0) {
      invalid.forEach(({ file, error }) => {
        toast.error(`${file.name}: ${error}`);
      });
    }

    if (valid.length > 0) {
      addFiles(valid);
    }
  };

  const { getInputProps, openFileDialog } = useFileInput(
    (newFiles) => handleFilesSelected(newFiles),
    {
      multiple: config.maxFiles > 1,
      accept: acceptStringFor(config.fileType)
    }
  );

  const handleUpload = async (extract = false) => {
    extractChoiceRef.current = extract;
    try {
      await uploadFiles();
      callbacks.onClose?.();
      clearFiles();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      callbacks.onClose?.();
      clearFiles();
    }
  };

  return {
    // File state
    files,
    hasFiles,
    isUploading,
    canUpload,

    // File operations
    removeFile,
    updateFileType,
    updateCertifiedCopy,
    handleFilesSelected,

    // Dialog operations
    handleUpload,
    handleClose,
    openFileDialog,
    getInputProps,

    // Refs
    fileInputRef
  };
}
